import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
} from '@angular/common/http';
import { BehaviorSubject, Observable, Subject, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { Credentials } from '../models/enum/credentials.enum';
import { DeviceUUID } from 'device-uuid';
import { AuthUtil } from '../utils/auth.utils';
import { AuthResponse } from '../models/interfaces/auth-response.interface';

@Injectable()
export class UnauthorizedInterceptor implements HttpInterceptor {
  private refreshTokenInProgress = false;
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);

  constructor(
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly authUtil: AuthUtil
  ) { }

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return next.handle(this.injectToken(request)).pipe(
      catchError((error) => {
        const isUnauthorized = error.status === 401;
        const isForbidden = error.status === 403;
        const errorMessage = error?.error?.message;

        const isExpiredToken = errorMessage === Credentials.EXPIRED_TOKEN;
        const isInvalidCredentials =
          errorMessage === Credentials.INVALID_CREDENTIALS ||
          errorMessage === Credentials.DEVICE_ID_NOT_INFORMED;

        if (isUnauthorized) {
          if (isExpiredToken) {
            return this.handleExpiredToken(request, next);
          }

          if (isInvalidCredentials) {
            this.toLoginPage();
          }
        }

        if (
          isForbidden &&
          this.router.url !== '/forgot-password' &&
          !error.url?.includes('agenda-cdn')
        ) {
          this.toLoginPage();
        }

        return throwError(error);
      })

    );
  }


  private toLoginPage(): void {
    this.authService.logout();
    if (this.router.url !== '/login') {
      this.router.navigate(['login']);
    }
  }

  private injectToken(request: HttpRequest<any>) {
    const accessToken = this.authUtil.getAccessToken();
    const deviceId = new DeviceUUID().get();
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${accessToken}`,
        'x-device-id': deviceId,
      },
    });
  }

  private handleExpiredToken(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (!this.refreshTokenInProgress) {
      this.refreshTokenInProgress = true;
      this.refreshTokenSubject.next(null);

      return this.authService.refreshToken().pipe(
        switchMap((res: AuthResponse) => {
          this.refreshTokenInProgress = false;

          this.authUtil.saveAccesToken(res.access_token);
          this.authUtil.saveRefreshToken(res.refreshToken);

          this.refreshTokenSubject.next(res.access_token);

          return next.handle(this.injectToken(request));
        }),
        catchError(err => {
          this.refreshTokenInProgress = false;
          this.toLoginPage();
          return throwError(err);
        })
      );
    }

    return this.refreshTokenSubject.pipe(
      filter(token => token !== null),
      take(1),
      switchMap(() => next.handle(this.injectToken(request)))
    );
  }
}
